<script setup>
import { emailValidator, requiredValidator } from '@/@core/utils/validators';
import { useAuthStore } from '@/composables/store/admin/useAuthStore';
import Logo from '@images/landing/logo.png';
import { computed, nextTick, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { VForm } from 'vuetify/components/VForm';

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();

definePage({ meta: { layout: 'blank', unauthenticatedOnly: true } });

// Form refs and state
const refVForm = ref(null);
const currentStep = ref(0);
const isPasswordVisible = ref(false);
const isPasswordConfirmationVisible = ref(false);

// Add animation constants
const slideTransition = ref({
  enter: 'slide-x-enter-active',
  leave: 'slide-x-leave-active'
});

// Update items to be more concise
const items = [
  { title: 'Account', icon: 'tabler-user' },
  { title: 'Company', icon: 'tabler-building-store' },
];

// Add utility functions for generating random data
const generateRandomData = () => {
  const randomId = Math.floor(Math.random() * 10000);
  const domains = ['example.com', 'test.com', 'demo.com', 'company.com'];
  const randomDomain = domains[Math.floor(Math.random() * domains.length)];
  const cities = ['New York', 'London', 'Tokyo', 'Paris', 'Singapore', 'Dubai'];
  const randomCity = cities[Math.floor(Math.random() * cities.length)];
  const states = ['New York', 'California', 'Texas', 'Florida', 'Illinois'];
  const randomState = states[Math.floor(Math.random() * states.length)];
  const companyTypes = ['Tech', 'Solutions', 'Industries', 'Systems', 'Group'];
  const randomCompanyType = companyTypes[Math.floor(Math.random() * companyTypes.length)];

  return {
    name: `Test User ${randomId}`,
    email: `user${randomId}@${randomDomain}`,
    password: 'Password123',
    password_confirmation: 'Password123',
    company_name: `${randomCompanyType} Company ${randomId}`,
    company_email: `contact${randomId}@${randomDomain}`,
    company_phone: `+1${Math.floor(Math.random() * 9000000000 + 1000000000)}`,
    company_registration_number: `REG${randomId}`,
    company_address_line1: `${Math.floor(Math.random() * 999) + 1} Business Street`,
    company_postal_code: Math.floor(Math.random() * 90000 + 10000).toString(),
    company_city: randomCity,
    company_state: randomState,
    company_country: 'United States',
  };
};

// Initialize form with empty data
const form = ref({
  name: '',
  email: '',
  password: '',
  password_confirmation: '',
  company_name: '',
  company_email: '',
  company_phone: '',
  company_registration_number: '',
  company_address_line1: '',
  company_postal_code: '',
  company_city: '',
  company_state: '',
  company_country: '',
});

// Add computed property for development mode
const isDevelopment = computed(() => {
  return process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'local';
});

// Update the auto-populate logic
onMounted(() => {
  if (isDevelopment.value) {
    form.value = generateRandomData();
  }
});

// Update regenerate function
const regenerateData = () => {
  if (isDevelopment.value) {
    form.value = generateRandomData();
  }
};

const countries = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda', 'Argentina',
  'Armenia', 'Australia', 'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados',
  'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina',
  'Botswana', 'Brazil', 'Brunei Darussalam', 'Bulgaria', 'Burkina Faso', 'Burundi', 'Cabo Verde',
  'Cambodia', 'Cameroon', 'Canada', 'Central African Republic', 'Chad', 'Chile', 'China',
  'Colombia', 'Comoros', 'Congo', 'Costa Rica', 'Croatia', 'Cuba', 'Cyprus', 'Czech Republic',
  'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic', 'Ecuador', 'Egypt', 'El Salvador',
  'Equatorial Guinea', 'Eritrea', 'Estonia', 'Eswatini', 'Ethiopia', 'Fiji', 'Finland', 'France',
  'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea',
  'Guinea-Bissau', 'Guyana', 'Haiti', 'Honduras', 'Hungary', 'Iceland', 'India', 'Indonesia',
  'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan',
  'Kenya', 'Kiribati', 'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia',
  'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg', 'Madagascar', 'Malawi', 'Malaysia',
  'Maldives', 'Mali', 'Malta', 'Marshall Islands', 'Mauritania', 'Mauritius', 'Mexico',
  'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar',
  'Namibia', 'Nauru', 'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria',
  'North Korea', 'North Macedonia', 'Norway', 'Oman', 'Pakistan', 'Palau', 'Palestine', 'Panama',
  'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania',
  'Russia', 'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines',
  'Samoa', 'San Marino', 'Sao Tome and Principe', 'Saudi Arabia', 'Senegal', 'Serbia',
  'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia',
  'South Africa', 'South Korea', 'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname',
  'Sweden', 'Switzerland', 'Syria', 'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Timor-Leste',
  'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu', 'Uganda',
  'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan',
  'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe'
].sort();

const onSubmit = async () => {
  const { valid } = await refVForm.value?.validate() || { valid: false };

  if (!valid) return;

  try {
    authStore.updateSignupUserData({ ...form.value });
    await authStore.signUp();
    await nextTick();
    router.replace({ name: 'admin-authentication-login' });
  } catch (error) {
    console.error('Error during sign-up:', error);
  }
};
</script>

<template>
  <VRow no-gutters class="auth-wrapper">
    <!-- Logo column -->
    <VCol md="4" class="d-none d-md-flex auth-logo-column">
      <div class="auth-logo-content">
        <VImg :src="Logo" class="auth-logo animate-float" />
        <p class="auth-logo-text text-brand">MARBLE SOLUTION</p>
      </div>
    </VCol>

    <!-- Form column -->
    <VCol cols="12" md="8" class="auth-card-v2 d-flex align-center justify-center pa-6">
      <VForm ref="refVForm" @submit.prevent="onSubmit" class="d-flex justify-center align-center w-100">
        <VCard flat class="mt-12 mt-sm-0 auth-card" max-width="700">
          <!-- Stepper -->
          <AppStepper v-model:current-step="currentStep" :items="items"
            :direction="$vuetify.display.smAndUp ? 'horizontal' : 'vertical'" icon-size="24"
            class="stepper-icon-step-bg mb-6" />

          <!-- Form content -->
          <VWindow v-model="currentStep" class="disable-tab-transition" :transition="slideTransition">
            <!-- Account Information -->
            <VWindowItem>
              <h5 class="text-h5 mb-1 animate-fade-in">Account Information</h5>
              <VRow dense>
                <!-- Account form fields with tighter spacing -->
                <VCol cols="12" sm="6">
                  <AppTextField v-model="form.name" label="Name" class="animate-slide-up"
                    :rules="[requiredValidator]" />
                </VCol>
                <VCol cols="12" sm="6">
                  <AppTextField v-model="form.email" label="Email" placeholder="johndoe@email.com"
                    :disabled="authStore.isSignupLoading" :rules="[requiredValidator, emailValidator]" />
                </VCol>
                <VCol cols="12" sm="6">
                  <AppTextField v-model="form.password" label="Password" placeholder="············"
                    :type="isPasswordVisible ? 'text' : 'password'"
                    :append-inner-icon="isPasswordVisible ? 'tabler-eye-off' : 'tabler-eye'"
                    :rules="[requiredValidator]" @click:append-inner="isPasswordVisible = !isPasswordVisible" />
                </VCol>
                <VCol cols="12" sm="6">
                  <AppTextField v-model="form.password_confirmation" label="Confirm Password" placeholder="············"
                    :type="isPasswordConfirmationVisible ? 'text' : 'password'"
                    :append-inner-icon="isPasswordConfirmationVisible ? 'tabler-eye-off' : 'tabler-eye'"
                    :rules="[requiredValidator]"
                    @click:append-inner="isPasswordConfirmationVisible = !isPasswordConfirmationVisible" />
                </VCol>
              </VRow>
            </VWindowItem>

            <!-- Company Information -->
            <VWindowItem>
              <h5 class="text-h5 mb-1 animate-fade-in">Company Information</h5>
              <VRow dense>
                <!-- Company form fields with tighter spacing -->
                <VCol cols="12" sm="6">
                  <AppTextField v-model="form.company_name" label="Company Name" class="animate-slide-up"
                    :rules="[requiredValidator]" />
                </VCol>
                <VCol cols="12" sm="6">
                  <AppTextField v-model="form.company_email" label="Company Email" placeholder="company@example.com"
                    :rules="[requiredValidator, emailValidator]" />
                </VCol>
                <VCol cols="12" sm="6">
                  <AppTextField v-model="form.company_phone" type="tel" label="Phone Number" placeholder="+1234567890"
                    :rules="[requiredValidator]" />
                </VCol>
                <VCol cols="12" sm="6">
                  <AppTextField v-model="form.company_registration_number" label="Registration Number"
                    placeholder="REG123456" :rules="[requiredValidator]" />
                </VCol>
                <VCol cols="12">
                  <AppTextField v-model="form.company_address_line1" label="Address Line 1" placeholder="Street address"
                    :rules="[requiredValidator]" />
                </VCol>
                <VCol cols="12" sm="2">
                  <AppTextField v-model="form.company_postal_code" label="Postal Code" placeholder="10001"
                    :rules="[requiredValidator]" />
                </VCol>
                <VCol cols="12" sm="5">
                  <AppTextField v-model="form.company_city" label="City" placeholder="Enter city"
                    :rules="[requiredValidator]" />
                </VCol>
                <VCol cols="12" sm="5">
                  <AppTextField v-model="form.company_state" label="State/District" placeholder="Enter state/district"
                    :rules="[requiredValidator]" />
                </VCol>
                <VCol cols="12" sm="6">
                  <AppCombobox v-model="form.company_country" :items="countries" label="Country"
                    placeholder="Select country" :rules="[requiredValidator]" clearable :menu-props="{ maxHeight: 300 }"
                    class="app-textfield" />
                </VCol>
              </VRow>
            </VWindowItem>
          </VWindow>

          <!-- Navigation buttons -->
          <div class="d-flex flex-wrap justify-center gap-x-2 gap-y-2 mt-6">
            <VBtn rounded="pill" color="secondary" variant="tonal" size="large" :disabled="currentStep === 0"
              @click="currentStep--" class="animate-slide-up">
              <VIcon icon="tabler-chevron-left" start class="flip-in-rtl" />
              Previous
            </VBtn>

            <VBtn v-if="items.length - 1 > currentStep" rounded="pill" color="primary" size="large"
              @click="currentStep++" class="animate-slide-up">
              Next
              <VIcon icon="tabler-chevron-right" end class="flip-in-rtl" />
            </VBtn>

            <VBtn v-else rounded="pill" color="primary" size="large" :loading="authStore.isSignupLoading" type="submit"
              class="animate-slide-up">
              Sign Up
              <VIcon icon="tabler-check" end />
            </VBtn>
          </div>
        </VCard>
      </VForm>
    </VCol>
  </VRow>

  <!-- Update the regenerate button -->
  <VBtn v-if="isDevelopment" icon color="primary" size="small" class="ma-2 position-absolute"
    style="inset-block-start: 0; inset-inline-end: 0;" @click="regenerateData">
    <VIcon icon="tabler-refresh" />
  </VBtn>
</template>

<style lang="scss">
@use "@core/scss/template/pages/page-auth.scss";

.auth-logo-column {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  margin: 0;
  background: #232323;
  color: white;
  min-block-size: 100vh;

  .auth-logo {
    display: block;
    inline-size: 100%;
    max-block-size: 200px;
    max-inline-size: 280px;
    object-fit: contain;
  }
}

.auth-logo-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0;
  gap: 0.75rem;
}

.auth-logo-text {
  margin: 0;
  font-size: clamp(1.5rem, 2vw + 1rem, 2.5rem);
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.auth-card {
  transition: all 0.3s ease;
}

// Animations
.animate-float {
  animation: float 6s ease-in-out infinite;
}

.animate-fade-in {
  animation: fadeIn 0.6s ease-out;
}

.animate-slide-up {
  animation: slideUp 0.4s ease-out;
}

.slide-x-enter-active,
.slide-x-leave-active {
  transition: all 0.3s ease-out;
}

.slide-x-enter-from {
  opacity: 0;
  transform: translateX(30px);
}

.slide-x-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

@keyframes float {
  0% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-20px);
  }

  100% {
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
